import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { formDateValidation } from "../../../../app/common";
import { useAppSelector } from "../../../../app/hooks";
import SkeletonWrapper from "../../../../common/components/functional/skeletonWrapper/SkeletonWrapper";

type Props = {
  existingTrainingTypes?: string[];
  isEditMode?: boolean;
};

const TrainingsForm = ({ existingTrainingTypes, isEditMode }: Props) => {
  const { t } = useTranslation(["trainings", "talentProfile", "common"]);

  const {
    control,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { defaultValues, errors },
  } = useFormContext();

  const completedValue = watch("completed");
  const trainingTypes = useAppSelector((state) => state.localized.training);
  const trainingOptions = Object.entries(trainingTypes);
  trainingOptions.sort((a, b) => a[1].localeCompare(b[1]));

  const handleCompletedAtChange = (checked: boolean) => {
    if (!checked) {
      setValue("completedAt", defaultValues?.completedAt, {
        shouldDirty: getValues("completedAt") !== defaultValues?.completedAt,
      });
    }
    trigger();
  };

  return (
    <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
      <Grid item textAlign="end" xs={4}>
        <Typography color="textSecondary">{t("training")}</Typography>
      </Grid>
      <Grid item xs={8}>
        <Controller
          name="type"
          control={control}
          rules={{
            required: { value: true, message: t("common:requiredField") },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              error={!!errors.type}
              disabled={isEditMode}
              fullWidth
              size="small"
              select
            >
              {trainingOptions.map(([type, value]) => (
                <MenuItem
                  key={type}
                  value={type}
                  disabled={existingTrainingTypes?.includes(type)}
                >
                  {value}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
      <Grid item textAlign="end" xs={4} />
      <Grid item xs={8}>
        <Box display="flex" width="100%" alignItems="center">
          <Controller
            name="completed"
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <FormControlLabel
                sx={{ minWidth: "190px" }}
                label={t("trainings:trainingCompleted")}
                control={
                  <Checkbox
                    {...rest}
                    checked={value}
                    onChange={(_, checked) => {
                      onChange(checked);
                      return handleCompletedAtChange(checked);
                    }}
                  />
                }
              />
            )}
          />
          <Controller
            name="completedAt"
            control={control}
            rules={{
              required: {
                value: completedValue,
                message: t("common:requiredField"),
              },
              validate: (date) => formDateValidation(date),
            }}
            render={({ field: { onChange, ...rest } }) => (
              <SkeletonWrapper
                width="135px"
                height="32px"
                value={completedValue}
              >
                <DesktopDatePicker
                  {...rest}
                  disableFuture
                  format="dd.MM.yyyy"
                  onChange={onChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: !!errors.completedAt,
                      sx: {
                        width: "135px",
                      },
                    },
                  }}
                />
              </SkeletonWrapper>
            )}
          />
        </Box>
      </Grid>
      <Grid item textAlign="end" xs={4}>
        <Typography color="textSecondary">
          {t("talentProfile:valid")}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Box display="flex" alignItems="center" gap="0.5rem">
          <Controller
            name="validTo"
            control={control}
            rules={{
              validate: (date) => formDateValidation(date),
            }}
            render={({ field: { onChange, ...rest } }) => (
              <DesktopDatePicker
                {...rest}
                format="dd.MM.yyyy"
                onChange={onChange}
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!errors.validTo,
                    sx: {
                      width: "135px",
                    },
                  },
                }}
              />
            )}
          />
          <Typography color="textSecondary">{t("common:until")}</Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default TrainingsForm;
